<script lang="ts">
  import { onMount } from "svelte";
  import type { CropRegion } from "./Image";
  import inView from "$lib/utils/inView";

  export let smImage: string;
  export let mdImage: string;
  export let lgImage = mdImage;
  export let xlImage = lgImage || mdImage;
  export let altText: string;
  export let imageConfig = {
    smSize: { width: 767, height: 767 },
    mdSize: { width: 1199, height: 782 },
    lgSize: { width: 1200, height: 480 },
    xlSize: { width: 1600, height: 480 },
  };
  export let fitOrFill: string = "fit";
  export let cover = false;
  export let noTransform = false;
  export let shopifyImage = false;
  export let eager = false;

  let pictureEl: HTMLPictureElement;
  let wrapperEl: HTMLDivElement;
  let loadingAttr: "eager" | "lazy" = eager ? "eager" : "lazy";
  let imageLoaded = false;
  let imageKey = "";

  $: if (smImage || mdImage) {
    lgImage = mdImage;
    xlImage = lgImage || mdImage;
  }

  $: if (smImage || mdImage) {
    imageLoaded = false;
    imageKey = `${smImage}-${mdImage}-${lgImage}-${xlImage}-${Date.now()}`;
  }

  // Extract blurhash from URL
  function extractBlurhash(image: string): string | null {
    if (!image) return null;
    const match = image.match(/blurhash=([^&]*)/);
    if (match && match[1]) {
      try {
        return decodeURIComponent(match[1]);
      } catch (e) {
        return match[1];
      }
    }
    return null;
  }

  // Generate placeholder
  function generatePlaceholderImage(image: string) {
    if (!image) return "";

    if (shopifyImage) {
      return `${image}?width=40`;
    }

    if (image.includes(".flite.io")) {
      return image;
    }

    const [base, rest] = image?.split("/upload/") || ["", ""];
    const parts = rest?.split("/");
    const versionOrIdIndex = parts?.findIndex(part => part.startsWith("v"));

    // Extract existing transformations (like crops)
    const existingTransforms =
      parts?.slice(0, versionOrIdIndex).join("/") || "";
    const versionAndId = parts?.slice(versionOrIdIndex).join("/");

    // Combine existing transforms with placeholder quality/size
    const placeholderTransforms = `f_auto,q_10${
      noTransform ? "" : `,c_fit,w_${40}`
    }`;

    // If there are existing transforms, append new ones with a slash
    const allTransforms = existingTransforms
      ? `${existingTransforms}/${placeholderTransforms}`
      : placeholderTransforms;

    return `${base}/upload/${allTransforms}/${versionAndId}`;
  }

  // Try to get blurhash first, fall back to placeholder image
  $: blurhash = extractBlurhash(smImage) || extractBlurhash(mdImage);
  $: placeholderUrl = generatePlaceholderImage(smImage);

  // For blurhash, we'll need to import and use the blurhash library
  let blurhashDataUrl: string | null = null;

  // Reset blurhash when source changes
  $: if (smImage || mdImage) {
    blurhashDataUrl = null;
  }

  $: if (blurhash && typeof window !== "undefined") {
    import("blurhash")
      .then(({ decode }) => {
        try {
          // Decode blurhash to pixels
          const pixels = decode(blurhash, 32, 32);

          // Create canvas and draw pixels
          const canvas = document.createElement("canvas");
          canvas.width = 32;
          canvas.height = 32;
          const ctx = canvas.getContext("2d");

          if (ctx) {
            const imageData = ctx.createImageData(32, 32);
            imageData.data.set(pixels);
            ctx.putImageData(imageData, 0, 0);

            // Convert to data URL
            blurhashDataUrl = canvas.toDataURL();
          }
        } catch (error) {
          console.warn("Failed to decode blurhash:", error);
          // Fall back to regular placeholder
          blurhashDataUrl = null;
        }
      })
      .catch(error => {
        console.warn("Failed to load blurhash library:", error);
      });
  }

  // Ensure the URL is properly formatted for CSS
  function escapeUrlForCss(url: string): string {
    if (!url) return "";
    // If it's a data URL or already has quotes, return as is
    if (url.startsWith("data:") || url.includes('"') || url.includes("'")) {
      return url;
    }
    // Otherwise, ensure special characters are escaped
    return url.replace(/'/g, "\\'").replace(/"/g, '\\"');
  }

  $: backgroundUrl = escapeUrlForCss(blurhashDataUrl || placeholderUrl);

  // Auto-detect above-fold images
  onMount(() => {
    if (!eager && pictureEl) {
      const rect = pictureEl.getBoundingClientRect();
      const isAboveFold = rect.top < window.innerHeight;
      if (isAboveFold) {
        // Change loading to eager for above-fold images
        const img = pictureEl.querySelector("img");
        if (img) {
          img.loading = "eager";
        }
      }
    }

    // Handle image load event
    const img = pictureEl?.querySelector("img");
    if (img) {
      const handleLoad = () => {
        imageLoaded = true;
        if (wrapperEl) {
          wrapperEl.classList.add("image-loaded");
        }
      };

      // Check if already loaded
      if (img.complete && img.naturalHeight !== 0) {
        handleLoad();
      } else {
        img.addEventListener("load", handleLoad);
        img.addEventListener("error", handleLoad); // Hide placeholder on error too
      }
    }
  });

  // Simple image transformation
  function transformImage(
    image: string,
    size: { width: number; height: number; crop?: CropRegion },
    doubleSize = false,
  ): string {
    const width = doubleSize ? size.width * 2 : size.width;
    const height = doubleSize ? size.height * 2 : size.height;

    if (shopifyImage) {
      const basePath = image.split("?")[0];
      const queryParams = new URLSearchParams(image.split("?")[1]);
      const fileExtension = basePath.split(".").pop() || "jpg";
      const baseWithoutExt = basePath.substring(0, basePath.lastIndexOf("."));
      return `${baseWithoutExt}_${width}x${height}${
        size.crop ? `_crop_${size.crop}` : ""
      }.${fileExtension}?${queryParams.toString()}`;
    }

    if (image.includes(".flite.io")) {
      return image;
    }

    const [base, rest] = image?.split("/upload/") || ["", ""];
    const parts = rest?.split("/");
    const versionOrIdIndex = parts?.findIndex(part => part.startsWith("v"));

    // Extract existing transformations
    const existingTransforms =
      parts?.slice(0, versionOrIdIndex).join("/") || "";
    const versionAndId = parts?.slice(versionOrIdIndex).join("/");

    // Combine existing transforms with new ones
    const newTransforms = `f_auto,q_80${
      noTransform ? "" : `,c_${fitOrFill},w_${width},h_${height}`
    }`;

    // If there are existing transforms, append new ones with a slash
    const allTransforms = existingTransforms
      ? `${existingTransforms}/${newTransforms}`
      : newTransforms;

    return `${base}/upload/${allTransforms}/${versionAndId}`;
  }

  $: xlSrcSet = `${transformImage(
    xlImage,
    imageConfig.xlSize,
  )} 1x, ${transformImage(xlImage, imageConfig.xlSize, true)} 2x`;
  $: lgSrcSet = `${transformImage(
    lgImage,
    imageConfig.lgSize,
  )} 1x, ${transformImage(lgImage, imageConfig.lgSize, true)} 2x`;
  $: mdSrcSet = `${transformImage(
    mdImage,
    imageConfig.mdSize,
  )} 1x, ${transformImage(mdImage, imageConfig.mdSize, true)} 2x`;
  $: smSrcSet = `${transformImage(
    smImage,
    imageConfig.smSize,
  )} 1x, ${transformImage(smImage, imageConfig.smSize, true)} 2x`;

  $: fallbackSrc = transformImage(smImage, imageConfig.smSize);

  let isInView = false;
</script>

<style lang="scss">
  .picture-wrapper {
    position: relative;
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
    overflow: hidden;
    display: block;
    // Ensure minimum height so background is visible
    min-height: 25px;

    // Blur effect for placeholder
    &::before {
      content: "";
      position: absolute;
      top: -10px;
      left: -10px;
      right: -10px;
      bottom: -10px;
      background-image: inherit;
      background-size: cover;
      background-position: center;
      filter: blur(20px);
      z-index: 1;
      opacity: 1;
      transition: opacity 0.3s ease-out;
    }

    &.image-loaded::before {
      opacity: 0;
    }
  }

  .cover {
    height: 100%;
    width: 100%;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      object-position: center;
    }
  }

  picture {
    position: relative;
    display: block;
    z-index: 2;
  }

  img {
    display: block;
    width: 100%;
    height: auto;
    max-width: 100%;
    position: relative;
    z-index: 2;
  }
</style>

{#key imageKey || `${smImage}-${mdImage}`}
  <div
    bind:this={wrapperEl}
    class="picture-wrapper inner {isInView ? 'fade-in' : ''}"
    class:image-loaded={imageLoaded}
    style="background-image: url('{backgroundUrl}')"
    use:inView={{ threshold: 0.1 }}
    on:intersect={({ detail }) => {
      if (detail) {
        isInView = true;
      }
    }}
  >
    <picture bind:this={pictureEl} class:cover={cover || shopifyImage}>
      <source media="(max-width: 767px)" srcset={smSrcSet} />
      <source
        media="(min-width: 768px) and (max-width: 1279px)"
        srcset={mdSrcSet}
        sizes="50vw"
      />
      <source
        media="(min-width: 1280px) and (max-width: 1399px)"
        srcset={lgSrcSet}
        sizes="33vw"
      />
      <source media="(min-width: 1400px)" srcset={xlSrcSet} sizes="25vw" />
      <img
        src={fallbackSrc}
        width={imageConfig.smSize.width}
        height={imageConfig.smSize.height}
        alt={altText}
        loading={loadingAttr}
      />
    </picture>
  </div>
{/key}
